import { useMemo } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { TIMESERIES_METRICS, type TimeseriesMetric } from '@/api/types'
import {
  overviewQuery,
  rangeToBounds,
  storageQuery,
  timeseriesQuery,
  topWorldsQuery,
} from '@/features/analytics/analytics.queries'
import { TrendChart } from '@/features/analytics/TrendChart'
import { AttentionList } from '@/features/analytics/AttentionList'
import { StorageBreakdown } from '@/features/analytics/StorageBreakdown'
import { formatBytes, formatCompact, formatDuration, formatNumber } from '@/lib/format'
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatCard,
} from '@/ui'
import { IconBolt, IconStorage, IconUsers, IconWorlds } from '@/icons'

/* Paired with their label keys rather than built as `range.${days}d`: a template literal
   is not a known key, so typing it would mean widening every t() call in the file. */
const RANGES = [
  { days: 7, labelKey: 'range.7d' },
  { days: 30, labelKey: 'range.30d' },
  { days: 90, labelKey: 'range.90d' },
  { days: 365, labelKey: 'range.365d' },
] as const

const searchSchema = z.object({
  metric: z.enum(TIMESERIES_METRICS).default('visits'),
  days: z.coerce.number().pipe(z.union([z.literal(7), z.literal(30), z.literal(90), z.literal(365)])).default(30),
})

export const Route = createFileRoute('/_authed/')({
  validateSearch: searchSchema,
  component: OverviewPage,
})

function OverviewPage() {
  const { t, i18n } = useTranslation()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const overview = useQuery(overviewQuery)
  const storage = useQuery(storageQuery)
  const topWorlds = useQuery(topWorldsQuery('visits', 8))

  const bounds = useMemo(() => rangeToBounds(search.days), [search.days])
  const series = useQuery(timeseriesQuery(search.metric, bounds.from, bounds.to, bounds.interval))

  if (overview.isPending) return <LoadingState />
  if (overview.isError) {
    return (
      <ErrorState
        error={overview.error}
        onRetry={() => {
          void overview.refetch()
        }}
      />
    )
  }

  const data = overview.data

  return (
    <>
      <PageHeader title={t('overview.title')} description={t('overview.subtitle')} />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label={t('overview.peopleTotal')}
          value={formatNumber(data.users.total, i18n.language)}
          sublabel={t('overview.peopleNew', { count: data.users.new7d })}
          icon={<IconUsers size={16} />}
        />
        <StatCard
          label={t('overview.dau')}
          value={formatNumber(data.active.dau, i18n.language)}
          sublabel={`${formatNumber(data.active.mau, i18n.language)} ${t('overview.mau').toLowerCase()}`}
          icon={<IconBolt size={16} />}
        />
        <StatCard
          label={t('overview.worldsTotal')}
          value={formatNumber(data.worlds.total, i18n.language)}
          sublabel={`${formatNumber(data.worlds.published, i18n.language)} ${t('overview.worldsPublished').toLowerCase()}`}
          icon={<IconWorlds size={16} />}
        />
        <StatCard
          label={t('overview.storageUsed')}
          value={formatBytes(data.storage.totalBytes, i18n.language)}
          sublabel={`${formatNumber(data.assets.total, i18n.language)} ${t('overview.assets').toLowerCase()}`}
          icon={<IconStorage size={16} />}
        />
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label={t('overview.liveInstances')}
          value={formatNumber(data.rooms.liveInstances, i18n.language)}
          sublabel={`${formatNumber(data.rooms.occupantsNow, i18n.language)} ${t('overview.occupantsNow').toLowerCase()}`}
        />
        <StatCard
          label={t('overview.visits24h')}
          value={formatNumber(data.rooms.visits24h, i18n.language)}
          sublabel={`${t('overview.medianSession')}: ${formatDuration(data.rooms.medianSessionMinutes, i18n.language)}`}
        />
        <StatCard
          label={t('overview.messages24h')}
          value={formatNumber(data.social.messages24h, i18n.language)}
          sublabel={`${formatNumber(data.social.worldChat24h, i18n.language)} ${t('overview.worldChat24h').toLowerCase()}`}
        />
        <StatCard
          label={t('overview.moderationToday')}
          value={formatNumber(data.moderation.actions24h, i18n.language)}
          sublabel={`${formatNumber(data.moderation.activeRestrictions, i18n.language)} ${t('overview.activeRestrictions').toLowerCase()}`}
          isInverted
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Card>
          <CardHeader
            title={t('overview.trendChart')}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor="metric-select">
                  {t('common.sortBy')}
                </label>
                <select
                  id="metric-select"
                  value={search.metric}
                  onChange={(event) => {
                    void navigate({
                      search: (prev) => ({
                        ...prev,
                        metric: event.target.value as TimeseriesMetric,
                      }),
                    })
                  }}
                  className="h-8 rounded-control border border-border-strong/60 bg-surface px-2 text-[0.8125rem] text-text"
                >
                  {TIMESERIES_METRICS.map((metric) => (
                    <option key={metric} value={metric}>
                      {t(`metric.${metric}`)}
                    </option>
                  ))}
                </select>

                <div className="flex rounded-control border border-border p-0.5" role="group" aria-label={t('range.label')}>
                  {RANGES.map((range) => (
                    <button
                      key={range.days}
                      type="button"
                      aria-pressed={search.days === range.days}
                      onClick={() => {
                        void navigate({ search: (prev) => ({ ...prev, days: range.days }) })
                      }}
                      className={
                        search.days === range.days
                          ? 'rounded-[0.375rem] bg-primary-subtle px-2 py-1 text-xs font-medium text-primary'
                          : 'rounded-[0.375rem] px-2 py-1 text-xs font-medium text-text-secondary hover:text-text'
                      }
                    >
                      {t(range.labelKey)}
                    </button>
                  ))}
                </div>
              </div>
            }
          />

          <div className="mt-4">
            {series.isPending ? (
              <LoadingState />
            ) : series.isError ? (
              <ErrorState
                error={series.error}
                onRetry={() => {
                  void series.refetch()
                }}
              />
            ) : (
              <TrendChart
                points={series.data.points}
                interval={bounds.interval}
                label={t(`metric.${search.metric}`)}
              />
            )}
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <AttentionList
            failedJobs={data.assets.processingFailed}
            orphaned={storage.data?.orphaned}
            hasFeaturedWorld={topWorlds.data?.items.some((world) => world.isFeatured) ?? true}
          />
          <StorageBreakdown data={storage.data} isLoading={storage.isPending} />
        </div>
      </div>

      <Card isFlush className="mt-4">
        <div className="p-5 pb-0">
          <CardHeader title={t('overview.topWorlds')} />
        </div>

        {topWorlds.isPending ? (
          <LoadingState />
        ) : topWorlds.data && topWorlds.data.items.length > 0 ? (
          <ul className="mt-3 divide-y divide-border">
            {topWorlds.data.items.map((world, index) => (
              <li key={world.slug}>
                <Link
                  to="/worlds/$slug"
                  params={{ slug: world.slug }}
                  className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-surface-sunken"
                >
                  <span
                    data-numeric
                    className="w-5 shrink-0 text-right text-xs font-medium text-text-muted"
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.8125rem] leading-5 font-medium text-text">
                      {world.title}
                    </span>
                    <span className="block truncate text-xs leading-4 text-text-secondary">
                      {world.ownerDisplayName}
                    </span>
                  </span>
                  {world.isFeatured ? <Badge tone="primary">{t('worlds.badgeFeatured')}</Badge> : null}
                  <span data-numeric className="shrink-0 text-[0.8125rem] text-text-secondary">
                    {formatCompact(world.visits, i18n.language)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title={t('overview.topWorldsEmpty')} />
        )}
      </Card>
    </>
  )
}
